import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { GetActiveAnnouncementsResponse, GetAllActiveStudiesResponse, RequestCount } from '@app/generated-api';
import { PageComponent } from '@app/v2/components/page/page.component';
import { MedpaceRequestDashboardCard2Component } from '@app/v2/components/request-dashboard-card/request-dashboard.component';
import { AnnouncementService } from '@app/v2/services/announcement/announcement.service';
import { ClinTrakDataService2 } from '@app/v2/services/clintrak/clintrak-data.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { MedpaceTableComponent } from '@components/molecules/table/table.component';
import { MdsExpansionPanelComponent } from '@medpacesoftwaredevelopment/designsystem';

import { LoadingComponent } from '@app/admin/manage/modals/loading-component/loading.component';
import { MedpaceFieldMap } from '@models/medpace-field-map';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { Observable, take, tap } from 'rxjs';
import { ActiveStudiesTableComponent } from '../active-studies-table/active-studies-table.component';
import { DisplayAnnouncementDialogComponent } from '../dialogs/display-announcement-dialog/display-announcement-dialog.component';

@Component({
    selector: 'medpace-main-dashboard',
    imports: [
        PageComponent,
        MdsExpansionPanelComponent,
        MedpaceTableComponent,
        ActiveStudiesTableComponent,
        MedpaceRequestDashboardCard2Component,
        AsyncPipe,
        LoadingComponent,
    ],
    templateUrl: './main-dashboard.page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainDashboardPageComponent {
    private readonly router = inject(Router);
    private readonly userService = inject(UserServiceV2);
    private readonly clinTrakService = inject(ClinTrakDataService2);
    private readonly requestService = inject(AdminRequestServices);
    private readonly matDialog = inject(MatDialog);
    private readonly announcementService = inject(AnnouncementService);

    hasPermissionToPendingStudies$ = this.userService.hasAllPermissions$(['study.clintrak.create']);

    pendingStudiesColumns: string[] = [
        'projectId',
        'sponsorName',
        'statusTypeName',
        'studyName',
        'studyNumber',
        'status',
    ];

    pendingStudiesColumnMap: MedpaceFieldMap[] = [
        { header: 'Project ID', name: 'projectId' },
        { header: 'Sponsor Name', name: 'sponsorName' },
        { header: 'ClinTrak Status', name: 'statusTypeName' },
        { header: 'Study Name', name: 'studyName' },
        { header: 'Study Number', name: 'studyNumber' },
        { header: 'Status', name: 'status' },
    ];

    activeClinTrakStudies$ = this.clinTrakService.getAllActiveStudies().pipe(take(1));

    requestsCount$: Observable<RequestCount[]> = this.requestService.getTotalAllRequestsCount().pipe(take(1));

    announcementsToDisplay$: Observable<GetActiveAnnouncementsResponse[]> = this.announcementService
        .getAnnouncementsToDisplay()
        .pipe(
            take(1),
            tap((announcements) => {
                if (announcements.length != 0) {
                    this.matDialog.open(DisplayAnnouncementDialogComponent, {
                        data: {
                            announcements: announcements,
                            showRedirectToRequests: false,
                        },
                        ...getModalConfig(ModalSize.Small),
                    });
                }
            })
        );

    goToEditStudy(event: GetAllActiveStudiesResponse) {
        let navigationExtras = <NavigationExtras>{
            state: {
                event: event,
            },
        };
        this.router.navigate(['/admin/newstudy'], navigationExtras);
    }

    goToNewStudy() {
        this.router.navigate(['/admin/newstudy']);
    }
}
